import { useId } from 'react';
import { Controller, type Control, type UseFormRegister } from 'react-hook-form';
import { ManagerCombobox } from './ManagerCombobox';
import { ClientEmailsInput } from './ClientEmailsInput';
import type { ManagerSuggestion } from '@/features/Vacations/hooks/data/useManagerSuggestions';
import type { VacationRequestFormValues } from '../../../models/vacation.schemas';
import {
    CLIENT_EMAIL_LABEL,
    MANAGER_EMAIL_LABEL,
    MANAGER_EMAIL_PLACEHOLDER,
} from '../vacationRequestForm.definitions';

export interface ContactFieldsProps {
    control: Control<VacationRequestFormValues>;
    register: UseFormRegister<VacationRequestFormValues>;
    getFieldError: (name: keyof VacationRequestFormValues) => string | undefined;
    isSubmitting: boolean;
    managerSuggestions: ManagerSuggestion[];
    hasMultipleAllocationManagers: boolean;
    currentCollaboratorId?: string | null;
    clientEmails: string[];
    clientEmailsError?: string;
    onAddClientEmail: (email: string) => void;
    onRemoveClientEmail: (index: number) => void;
}

export function ContactFields({
    control,
    register,
    getFieldError,
    isSubmitting,
    managerSuggestions,
    hasMultipleAllocationManagers,
    currentCollaboratorId,
    clientEmails,
    clientEmailsError,
    onAddClientEmail,
    onRemoveClientEmail,
}: ContactFieldsProps) {
    const managerEmailError = getFieldError('managerEmail');
    const managerId = useId();

    return (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2" data-tour="vacation-form-contacts">
            <div>
                <label
                    htmlFor={managerId}
                    className="block text-sm font-medium text-raro-gray-700 font-montserrat"
                >
                    {MANAGER_EMAIL_LABEL}
                </label>
                <Controller
                    name="managerEmail"
                    control={control}
                    render={({ field }) => (
                        <ManagerCombobox
                            id={managerId}
                            suggestions={managerSuggestions}
                            value={field.value}
                            onChange={field.onChange}
                            excludeIds={currentCollaboratorId ? [currentCollaboratorId] : undefined}
                            disabled={isSubmitting}
                            placeholder={MANAGER_EMAIL_PLACEHOLDER}
                            hasMultipleAllocationManagers={hasMultipleAllocationManagers}
                        />
                    )}
                />
                {managerEmailError && (
                    <p className="mt-1 text-xs text-red-600 font-montserrat">{managerEmailError}</p>
                )}
            </div>

            <div>
                <label className="block text-sm font-medium text-raro-gray-700 font-montserrat">
                    {CLIENT_EMAIL_LABEL}
                </label>
                <div className="mt-1">
                    <ClientEmailsInput
                        emails={clientEmails}
                        onAdd={onAddClientEmail}
                        onRemove={onRemoveClientEmail}
                        disabled={isSubmitting}
                        error={clientEmailsError}
                    />
                </div>
            </div>
        </div>
    );
}

export default ContactFields;
